import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Button,
    TextField,
    CircularProgress,
    Box,
    Typography,
    Icon,
    Paper,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/id";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { API_URL } from "../api";
import { CustomModal } from "../components/CustomModal";

// Helper function
const formatDisplay = (isoStr) => {
    if (!isoStr) return null;
    const d = new Date(isoStr);
    if (isNaN(d)) return isoStr;
    return d.toLocaleString("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};



const toMysqlDatetime = (date) => date.toISOString().slice(0, 19).replace("T", " ");

const dateLabel = (statusSesudah) => {
    if (statusSesudah === "diterima") return "Tanggal Diterima *";
    if (statusSesudah === "maintenance") return "Tanggal Mulai Maintenance *";
    if (statusSesudah === "selesai") return "Tanggal Selesai *";
    return "Tanggal *";
};

function TimestampRow({ icon, label, value }) {
    if (!value) return null;
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 1.5 }}>
            <Typography sx={{ fontSize: 16, mr: 1.5, mt: 0.5 }}>{icon}</Typography>
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 0.5 }}>{label}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                    {formatDisplay(value)}
                </Typography>
            </Box>
        </Box>
    );
}

export default function FormFollowupKerusakan() {
    const { id: reportId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const { currentStatus = "menunggu" } = location.state || {};

    const [namaPenerima, setNamaPenerima] = useState("");
    const [namaMaintenance, setNamaMaintenance] = useState("");
    const [namaSelesai, setNamaSelesai] = useState("");
    const [tindakanLanjutan, setTindakanLanjutan] = useState("");
    const [catatan, setCatatan] = useState("");

    const [existingFollowup, setExistingFollowup] = useState(null);
    const [loadingData, setLoadingData] = useState(false);

    const getStatusSesudah = () => {
        switch (currentStatus) {
            case "menunggu":
                return "diterima";
            case "diterima":
                return "maintenance";
            case "maintenance":
                return "selesai";
            default:
                return "diterima";
        }
    };
    const statusSesudah = getStatusSesudah();

    useEffect(() => {
        const loadUserName = () => {
            try {
                const userString = localStorage.getItem("user");
                if (userString) {
                    const userData = JSON.parse(userString);
                    const name = userData.nama;
                    if (name) {
                        if (statusSesudah === "diterima") setNamaPenerima((prev) => prev || name);
                        if (statusSesudah === "maintenance") setNamaMaintenance((prev) => prev || name);
                        if (statusSesudah === "selesai") setNamaSelesai((prev) => prev || name);
                    }
                }
            } catch (e) {
                console.warn("Gagal membaca user:", e);
            }
        };
        loadUserName();
    }, [statusSesudah]);

    const [selectedDate, setSelectedDate] = useState(dayjs());

    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalConfig, setModalConfig] = useState({
        title: "",
        message: "",
        type: "info",
        buttons: [],
    });

    const showModal = (title, message, type = "info", buttons = []) => {
        setModalConfig({ title, message, type, buttons });
        setModalVisible(true);
    };

    const buttonConfig = (() => {
        switch (statusSesudah) {
            case "diterima":
                return { label: "✔ TERIMA LAPORAN", color: "#27ae60" };
            case "maintenance":
                return { label: "🔧 MULAI MAINTENANCE", color: "#e67e22" };
            case "selesai":
                return { label: "✅ TANDAI SELESAI", color: "#2980b9" };
            default:
                return { label: "✔ SIMPAN", color: "#27ae60" };
        }
    })();

    useEffect(() => {
        if (currentStatus === "menunggu") return;
        const fetchFollowup = async () => {
            setLoadingData(true);
            try {
                const token = localStorage.getItem("userToken");
                const res = await axios.get(`${API_URL}/alat/followup/${reportId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.data.success && res.data.data) {
                    const d = res.data.data;
                    setExistingFollowup(d);
                    if (d.nama_penerima) setNamaPenerima(d.nama_penerima);
                    if (d.nama_maintenance) setNamaMaintenance(d.nama_maintenance);
                    if (d.nama_selesai) setNamaSelesai(d.nama_selesai);
                    if (d.tindakan_lanjutan) setTindakanLanjutan(d.tindakan_lanjutan);
                    if (d.catatan) setCatatan(d.catatan);
                }
            } catch (e) {
                console.warn("Gagal fetch followup lama:", e);
            } finally {
                setLoadingData(false);
            }
        };
        fetchFollowup();
    }, [reportId, currentStatus]);

    const validateForm = () => {
        const namaPelaku =
            statusSesudah === "diterima"
                ? namaPenerima
                : statusSesudah === "maintenance"
                    ? namaMaintenance
                    : namaSelesai;

        if (!namaPelaku.trim() || !tindakanLanjutan.trim() || !selectedDate) {
            showModal(
                "Data Belum Lengkap",
                "Harap isi Nama Penerima / Penanggung Jawab, Tanggal, dan Tindakan Lanjutan.",
                "warning"
            );
            return false;
        }
        return true;
    };

    const showConfirmationModal = () => {
        if (!validateForm()) return;
        showModal(
            "Konfirmasi",
            `Status laporan akan berubah dari "${currentStatus}" menjadi "${statusSesudah}". Lanjutkan?`,
            "info",
            [
                { text: "Batal", onPress: () => setModalVisible(false), color: "#757575" },
                { text: "Ya, Simpan", onPress: handleSubmit, color: "#1976d2" },
            ]
        );
    };

    const handleSubmit = async () => {
        setModalVisible(false);
        setLoading(true);

        const isFirstFollowup = currentStatus === "menunggu";
        const method = isFirstFollowup ? "post" : "put";

        try {
            const token = localStorage.getItem("userToken");
            if (!token) {
                showModal("Sesi Habis", "Silakan login kembali untuk melanjutkan.", "warning");
                setLoading(false);
                return;
            }

            const response = await axios({
                method,
                url: `${API_URL}/alat/followup`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                data: {
                    kondisi_alat_id: reportId,
                    nama_penerima: statusSesudah === "diterima" ? namaPenerima.trim() : undefined,
                    nama_maintenance:
                        statusSesudah === "maintenance" ? namaMaintenance.trim() : undefined,
                    nama_selesai: statusSesudah === "selesai" ? namaSelesai.trim() : undefined,
                    tindakan_lanjutan: tindakanLanjutan.trim(),
                    catatan: catatan.trim(),
                    status_sebelum: currentStatus,
                    status_sesudah: statusSesudah,
                    tanggal_input: toMysqlDatetime(selectedDate.toDate()),
                },
            });

            showModal(
                "Berhasil!",
                `Status laporan diperbarui menjadi "${statusSesudah}".`,
                "success",
                [
                    {
                        text: "OK",
                        onPress: () => {
                            setModalVisible(false);
                            navigate(-1);
                        },
                        color: "#1976d2",
                    },
                ]
            );
        } catch (error) {
            console.error("Error followup:", error);
            if (error.response?.status === 401) {
                showModal("Sesi Habis", "Token tidak valid. Silakan login kembali.", "warning");
            } else if (error.response?.status === 403) {
                showModal(
                    "Akses Ditolak",
                    error.response?.data?.message || "Hanya Superintendent yang dapat memproses laporan ini.",
                    "error"
                );
            } else {
                showModal(
                    "Gagal",
                    error.response?.data?.error || error.response?.data?.message || "Terjadi kesalahan jaringan",
                    "error"
                );
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading || loadingData) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress size={40} />
                {loadingData && (
                    <Typography sx={{ mt: 2, color: "text.secondary" }}>
                        Memuat data followup...
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <div className="form-container" style={{ backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
            <header className="app-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate(-1)}>
                        <Icon>arrow_back</Icon>
                    </button>
                    <h1 className="header-title"> Follow Up Kerusakan</h1>
                    <div className="header-spacer"></div>
                </div>
            </header>

            <Box sx={{ p: 2 }}>
                <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2, mb: 3 }}>
                    {/* Banner status transition */}
                    <Box
                        sx={{
                            backgroundColor: "#e8f4fd",
                            borderLeft: "4px solid #5eb5eb",
                            borderRadius: "8px",
                            p: 2,
                            mb: 2,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: "#333", mb: 1 }}>
                            📋 ID Laporan: <Box component="span" sx={{ fontWeight: "bold", color: "#1565c0" }}>#{reportId}</Box>
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box sx={{ backgroundColor: "#fdebd0", borderRadius: "12px", px: 1.5, py: 0.5 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: "bold", textTransform: "capitalize", color: "#e67e22" }}>
                                    {currentStatus}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 16, color: "#888", fontWeight: "bold", mx: 1 }}> → </Typography>
                            <Box
                                sx={{
                                    backgroundColor:
                                        statusSesudah === "diterima"
                                            ? "#d5f5e3"
                                            : statusSesudah === "maintenance"
                                                ? "#fdebd0"
                                                : "#d6eaf8",
                                    borderRadius: "12px",
                                    px: 1.5,
                                    py: 0.5,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: "bold",
                                        textTransform: "capitalize",
                                        color:
                                            statusSesudah === "diterima"
                                                ? "#27ae60"
                                                : statusSesudah === "maintenance"
                                                    ? "#e67e22"
                                                    : "#2980b9",
                                    }}
                                >
                                    {statusSesudah}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Timestamp lama (read-only) */}
                    {existingFollowup && (existingFollowup.tanggal_terima || existingFollowup.tanggal_mulai) && (
                        <Box
                            sx={{
                                backgroundColor: "#f0fdf4",
                                borderRadius: "8px",
                                mb: 2,
                                p: 2,
                                border: "1px solid #bbf7d0",
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontSize: 12,
                                    fontWeight: "bold",
                                    color: "#166534",
                                    mb: 1.5,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                }}
                            >
                                📌 Riwayat Waktu
                            </Typography>
                            <TimestampRow icon="📥" label="Diterima" value={existingFollowup.tanggal_terima} />
                            <TimestampRow icon="🔧" label="Mulai Maintenance" value={existingFollowup.tanggal_mulai} />
                        </Box>
                    )}

                    {/* Form fields */}
                    <Box sx={{ mt: 2 }}>
                        {statusSesudah === "diterima" && (
                            <TextField
                                fullWidth
                                label="Nama Penerima Laporan *"
                                placeholder="Nama yang menerima laporan ini"
                                value={namaPenerima}
                                onChange={(e) => setNamaPenerima(e.target.value)}
                                margin="normal"
                                variant="outlined"
                            />
                        )}
                        {statusSesudah === "maintenance" && (
                            <TextField
                                fullWidth
                                label="Nama PIC Maintenance *"
                                placeholder="Nama yang mengirim/memulai maintenance"
                                value={namaMaintenance}
                                onChange={(e) => setNamaMaintenance(e.target.value)}
                                margin="normal"
                                variant="outlined"
                            />
                        )}
                        {statusSesudah === "selesai" && (
                            <TextField
                                fullWidth
                                label="Nama yang Menyelesaikan *"
                                placeholder="Nama yang membuat status selesai"
                                value={namaSelesai}
                                onChange={(e) => setNamaSelesai(e.target.value)}
                                margin="normal"
                                variant="outlined"
                            />
                        )}

                        <TextField
                            fullWidth
                            label="Tindakan Lanjutan *"
                            placeholder="Jelaskan tindakan yang dilakukan"
                            value={tindakanLanjutan}
                            onChange={(e) => setTindakanLanjutan(e.target.value)}
                            multiline
                            rows={3}
                            margin="normal"
                            variant="outlined"
                        />

                        <TextField
                            fullWidth
                            label="Catatan (Opsional)"
                            placeholder="Catatan atau keterangan tambahan"
                            value={catatan}
                            onChange={(e) => setCatatan(e.target.value)}
                            multiline
                            rows={2}
                            margin="normal"
                            variant="outlined"
                        />

                        <Box sx={{ mt: 3, mb: 1 }}>
                            <DateTimePicker
                                label={dateLabel(statusSesudah)}
                                value={selectedDate}
                                onChange={(newValue) => setSelectedDate(newValue)}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </Box>
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        onClick={showConfirmationModal}
                        sx={{
                            mt: 4,
                            py: 1.5,
                            backgroundColor: buttonConfig.color,
                            "&:hover": { backgroundColor: buttonConfig.color, filter: "brightness(0.9)" },
                            textTransform: "none",
                            fontWeight: "bold",
                            fontSize: 16,
                            borderRadius: "8px",
                        }}
                    >
                        {buttonConfig.label}
                    </Button>
                </Paper>
            </Box>

            <CustomModal
                visible={modalVisible}
                onClose={() => setModalVisible(false)}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
                buttons={modalConfig.buttons}
            />
        </div>
    );
}